import { useEffect, useRef } from 'react';
import { DRAWER_HEIGHT_CSS_VAR } from '../../../../constants';

/**
 * @param heightPx The sheet's laid-out height (`--rsbs-overlay-h`).
 * @param visibleHeightPx How many pixels of the sheet are on screen, measured
 *   up from the bottom of the viewport. `0` while closed or staged off screen.
 */
type DrawerHeightChangeHandler = (
  heightPx: number,
  visibleHeightPx: number,
) => void;

export const useDrawerHeightListener = (
  observedEl: Element | null,
  targetEl: HTMLElement | null,
  onHeightChange?: DrawerHeightChangeHandler,
): void => {
  const lastValueRef = useRef('');
  // Keep the latest callback in a ref so the observer doesn't need to be
  // re-attached whenever the caller passes a new function identity.
  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;

  useEffect(() => {
    if (!observedEl || (!targetEl && !onHeightChangeRef.current)) {
      return undefined;
    }

    lastValueRef.current = '';

    const checkOverlayHeight = (): void => {
      const computedStyle = getComputedStyle(observedEl);
      const value = computedStyle.getPropertyValue('--rsbs-overlay-h');
      // `react-spring-bottom-sheet` pins the height to the target snap point
      // for the whole open/close transition ("to avoid animating `height` on
      // open") and animates `--rsbs-overlay-translate-y` instead, so the height
      // alone reports the final position from the first frame.
      const translateY = computedStyle.getPropertyValue(
        '--rsbs-overlay-translate-y',
      );
      // Opening also starts with the sheet fully laid out at `opacity: 0`, so
      // geometry alone can't tell "on screen" from "staged off screen". Only
      // ever 0 or 1, never animated.
      const isRendered = computedStyle.opacity !== '0';

      const nextValue = `${value}|${translateY}|${isRendered}`;

      if (value && nextValue !== lastValueRef.current) {
        lastValueRef.current = nextValue;
        const trimmed = value.trim();
        const heightPx = Number.parseFloat(trimmed) || 0;
        // Overshoot past the max snap point translates negatively, which
        // legitimately exceeds the laid-out height.
        const visibleHeightPx = isRendered
          ? Math.max(heightPx - (Number.parseFloat(translateY) || 0), 0)
          : 0;

        targetEl?.style.setProperty(DRAWER_HEIGHT_CSS_VAR, trimmed);
        onHeightChangeRef.current?.(heightPx, visibleHeightPx);
      }
    };

    // Initial read
    checkOverlayHeight();

    const observer = new MutationObserver(checkOverlayHeight);

    observer.observe(observedEl, {
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, [observedEl, targetEl]);
};
